// ============================================================
// Commission — Tori's commission, month by month
// Visible only to COMMISSION_VIEWERS; editable only by COMMISSION_EDITORS.
// The database enforces the same thing with RLS (commission_setup.sql).
// ============================================================
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, X, Trash2, ChevronLeft, ChevronRight, Link2 } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import {
  canEditCommission, cellFor, dealStatus, dealWindow, dealTouchesYear, retainerFor,
  terminationDate, commissionFor, monthKey, monthStart, monthLabel, currentMonthKey,
  fmtMoney, COMMISSION_MONTHS,
} from './commissionConfig'

const fmtDate = (d) => {
  if (!d) return ''
  const dt = new Date(String(d).slice(0, 10) + 'T12:00:00')
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_STYLE = {
  active:     { label: 'Active',     bg: 'var(--lightgreen-bg)', color: 'var(--green)' },
  upcoming:   { label: 'Upcoming',   bg: 'var(--lightblue-bg)',  color: 'var(--navy-light)' },
  complete:   { label: 'Complete',   bg: 'var(--cream-dark)',    color: 'var(--text-muted)' },
  terminated: { label: 'Terminated', bg: 'var(--red-bg)',        color: 'var(--red)' },
}

export default function CommissionView({ allClients = [], currentUser }) {
  const toast = useToast()
  const canEdit = canEditCommission(currentUser?.id)
  const nowKey = currentMonthKey()
  const [year, setYear] = useState(Number(nowKey.slice(0, 4)))
  const [loading, setLoading] = useState(true)
  const [deals, setDeals] = useState([])
  const [changes, setChanges] = useState([])
  const [entries, setEntries] = useState([])
  const [bonuses, setBonuses] = useState([])
  const [payouts, setPayouts] = useState([])
  const [openCell, setOpenCell] = useState(null)     // { deal, key }
  const [openDeal, setOpenDeal] = useState(null)     // deal row, or {} for new
  const [openMonth, setOpenMonth] = useState(null)   // 'YYYY-MM'

  const load = useCallback(async () => {
    const [d, c, e, b, p] = await Promise.all([
      supabase.from('commission_deals').select('*').order('start_date'),
      supabase.from('commission_retainer_changes').select('*').order('effective_month'),
      supabase.from('commission_month_entries').select('*'),
      supabase.from('commission_bonuses').select('*').order('month'),
      supabase.from('commission_payouts').select('*'),
    ])
    const err = [d, c, e, b, p].find(r => r.error)
    if (err) toast('Could not load commission: ' + err.error.message, 'error')
    setDeals(d.data || [])
    setChanges(c.data || [])
    setEntries(e.data || [])
    setBonuses(b.data || [])
    setPayouts(p.data || [])
    setLoading(false)
  }, [toast])

  useEffect(() => { load() }, [load])

  const clientById = useMemo(() => {
    const m = {}
    allClients.forEach(c => { m[c.id] = c })
    return m
  }, [allClients])

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`),
    [year]
  )

  const cell = useCallback((deal, key) => cellFor(deal, key, {
    changes: changes.filter(c => c.deal_id === deal.id),
    entry: entries.find(e => e.deal_id === deal.id && monthKey(e.month) === key) || null,
    client: deal.client_id ? clientById[deal.client_id] : null,
  }), [changes, entries, clientById])

  const rows = useMemo(
    () => deals.filter(d => dealTouchesYear(d, year, entries)),
    [deals, year, entries]
  )

  const bonusFor = (key) => bonuses.filter(b => monthKey(b.month) === key).reduce((s, b) => s + Number(b.amount), 0)
  const payoutFor = (key) => payouts.find(p => monthKey(p.month) === key) || null
  const monthTotal = (key) => deals.reduce((s, d) => s + cell(d, key).amount, 0) + bonusFor(key)

  const yearTotal = months.reduce((s, k) => s + monthTotal(k), 0)
  const yearPaid = months.filter(k => payoutFor(k)?.paid_on).reduce((s, k) => s + monthTotal(k), 0)
  const inWindowNow = deals.filter(d => dealStatus(d, d.client_id ? clientById[d.client_id] : null) === 'active').length
  const unlinked = deals.filter(d => !d.client_id && dealStatus(d, null) !== 'complete' && dealStatus(d, null) !== 'terminated').length

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading commission…</div>

  return (
    <div>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button className="btn-icon" onClick={() => setYear(y => y - 1)} title="Previous year"><ChevronLeft size={16} /></button>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, minWidth: 56, textAlign: 'center' }}>{year}</span>
          <button className="btn-icon" onClick={() => setYear(y => y + 1)} title="Next year"><ChevronRight size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span><strong>{fmtMoney(monthTotal(nowKey))}</strong> for {monthLabel(nowKey)}</span>
          <span><strong>{fmtMoney(yearTotal)}</strong> in {year} ({fmtMoney(yearPaid)} paid)</span>
          <span><strong>{inWindowNow}</strong> client{inWindowNow === 1 ? '' : 's'} earning now</span>
        </div>
        {canEdit && (
          <button className="btn btn-gold btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setOpenDeal({})}>
            <Plus size={13} /> Add client
          </button>
        )}
      </div>

      {canEdit && unlinked > 0 && (
        <div className="card" style={{ marginBottom: 12, background: 'var(--cream)', border: '1px solid var(--border-light)' }}>
          <div className="card-body" style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            <Link2 size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
            {unlinked} client{unlinked === 1 ? ' isn\'t' : 's aren\'t'} linked to a client in the app yet, so archiving them won't stop commission.
            Click the name to link it.
          </div>
        </div>
      )}

      {/* The grid */}
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="task-table" style={{ width: '100%', minWidth: 1180, fontSize: 12.5, borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', left: 0, background: 'var(--white, #fff)', zIndex: 2, minWidth: 210 }}>Client</th>
              <th>Start</th>
              <th style={{ textAlign: 'right' }}>Retainer</th>
              {months.map(k => (
                <th key={k} style={{ textAlign: 'right', color: k === nowKey ? 'var(--mauve)' : undefined }}>{monthLabel(k, false)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={15} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No commission in {year}.</td></tr>
            )}
            {rows.map(deal => {
              const client = deal.client_id ? clientById[deal.client_id] : null
              const status = dealStatus(deal, client)
              const st = STATUS_STYLE[status]
              const dealChanges = changes.filter(c => c.deal_id === deal.id)
              const currentRetainer = retainerFor(deal, dealChanges, nowKey)
              return (
                <tr key={deal.id}>
                  <td style={{ position: 'sticky', left: 0, background: 'var(--white, #fff)', zIndex: 1, cursor: 'pointer' }}
                    onClick={() => setOpenDeal(deal)} title={deal.notes || 'Open details'}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{deal.client_name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                      {!deal.client_id && <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Not linked</span>}
                      {dealChanges.length > 0 && <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Retainer changed</span>}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{fmtDate(deal.start_date)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(currentRetainer)}</td>
                  {months.map(k => {
                    const c = cell(deal, k)
                    const clickable = c.state !== 'outside' || c.note || c.hasOverride
                    const future = k > nowKey
                    return (
                      <td key={k}
                        onClick={clickable ? () => setOpenCell({ deal, key: k }) : undefined}
                        title={c.note || undefined}
                        style={{
                          textAlign: 'right', whiteSpace: 'nowrap', cursor: clickable ? 'pointer' : 'default',
                          background: c.state === 'active' ? (future ? 'transparent' : 'var(--lightgreen-bg)') : undefined,
                          color: c.state === 'terminated' ? 'var(--red)' : future ? 'var(--text-muted)' : 'var(--text-primary)',
                          fontStyle: future && c.state === 'active' ? 'italic' : undefined,
                          position: 'relative',
                        }}>
                        {c.state === 'terminated' && !c.hasOverride
                          ? <span style={{ fontSize: 10.5, fontWeight: 600 }}>Terminated</span>
                          : (c.state !== 'outside' || c.hasOverride)
                            ? <span style={{ borderBottom: c.hasOverride ? '1.5px dotted var(--mauve)' : undefined }}>{fmtMoney(c.amount)}</span>
                            : ''}
                        {c.note && (
                          <span style={{ position: 'absolute', top: 4, right: 3, width: 5, height: 5, borderRadius: '50%', background: 'var(--mauve)' }} />
                        )}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ position: 'sticky', left: 0, background: 'var(--white, #fff)', fontWeight: 600 }}>Bonus</td>
              {months.map(k => {
                const b = bonusFor(k)
                return (
                  <td key={k} onClick={() => setOpenMonth(k)} style={{ textAlign: 'right', cursor: 'pointer', color: b ? 'var(--mauve)' : 'var(--text-muted)' }}>
                    {b ? fmtMoney(b) : canEdit ? '+' : ''}
                  </td>
                )
              })}
            </tr>
            <tr style={{ background: 'var(--cream)' }}>
              <td colSpan={3} style={{ position: 'sticky', left: 0, background: 'var(--cream)', fontWeight: 700 }}>Total</td>
              {months.map(k => {
                const t = monthTotal(k)
                return <td key={k} style={{ textAlign: 'right', fontWeight: 700 }}>{t ? fmtMoney(t) : ''}</td>
              })}
            </tr>
            <tr>
              <td colSpan={3} style={{ position: 'sticky', left: 0, background: 'var(--white, #fff)', fontWeight: 600 }}>Paid</td>
              {months.map(k => {
                const p = payoutFor(k)
                return (
                  <td key={k} onClick={() => setOpenMonth(k)} title={p?.note || undefined}
                    style={{ textAlign: 'right', cursor: 'pointer', fontSize: 11.5, whiteSpace: 'nowrap',
                      color: p?.paid_on ? 'var(--green)' : 'var(--text-muted)', position: 'relative' }}>
                    {p?.paid_on ? fmtDate(p.paid_on).replace(/, \d{4}$/, '') : canEdit && monthTotal(k) ? 'Mark paid' : ''}
                    {p?.note && <span style={{ position: 'absolute', top: 4, right: 3, width: 5, height: 5, borderRadius: '50%', background: 'var(--mauve)' }} />}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.6 }}>
        5% of the monthly retainer for the first {COMMISSION_MONTHS} months, starting the month of the start date.
        Nothing for the month a client terminates. Italic amounts are upcoming months; a dotted underline means the amount was set by hand; a dot means there's a note.
      </div>

      {openCell && (
        <CellModal canEdit={canEdit} deal={openCell.deal} monthKeyStr={openCell.key}
          info={cell(openCell.deal, openCell.key)}
          retainer={retainerFor(openCell.deal, changes.filter(c => c.deal_id === openCell.deal.id), openCell.key)}
          entry={entries.find(e => e.deal_id === openCell.deal.id && monthKey(e.month) === openCell.key) || null}
          onClose={() => setOpenCell(null)} onSaved={() => { setOpenCell(null); load() }} />
      )}
      {openMonth && (
        <MonthModal canEdit={canEdit} monthKeyStr={openMonth} total={monthTotal(openMonth)}
          payout={payoutFor(openMonth)} bonuses={bonuses.filter(b => monthKey(b.month) === openMonth)}
          onClose={() => setOpenMonth(null)} onSaved={load} />
      )}
      {openDeal && (
        <DealModal canEdit={canEdit} deal={openDeal} allClients={allClients} currentUser={currentUser}
          client={openDeal.client_id ? clientById[openDeal.client_id] : null}
          changes={changes.filter(c => c.deal_id === openDeal.id)}
          onClose={() => setOpenDeal(null)} onSaved={() => { setOpenDeal(null); load() }} onChangesSaved={load} />
      )}
    </div>
  )
}

// ── One client, one month: manual amount + note ─────────────────────────────
function CellModal({ canEdit, deal, monthKeyStr, info, retainer, entry, onClose, onSaved }) {
  const toast = useToast()
  const [amount, setAmount] = useState(entry?.amount_override ?? '')
  const [note, setNote] = useState(entry?.note || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    const override = amount === '' || amount === null ? null : Number(amount)
    const trimmed = note.trim()
    let error
    if (override === null && !trimmed) {
      ({ error } = await supabase.from('commission_month_entries').delete()
        .eq('deal_id', deal.id).eq('month', monthStart(monthKeyStr)))
    } else {
      ({ error } = await supabase.from('commission_month_entries').upsert({
        deal_id: deal.id, month: monthStart(monthKeyStr), amount_override: override,
        note: trimmed || null, updated_at: new Date().toISOString(),
      }, { onConflict: 'deal_id,month' }))
    }
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Saved', 'success')
    onSaved()
  }

  const stateLine = info.state === 'active'
    ? `Month ${info.monthNumber} of ${COMMISSION_MONTHS} · 5% of ${fmtMoney(retainer)} = ${fmtMoney(info.ruleAmount)}`
    : info.state === 'terminated'
      ? `Month ${info.monthNumber} of ${COMMISSION_MONTHS} · terminated, so nothing is owed`
      : 'Outside the 6-month window'

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <span className="modal-title">{deal.client_name} · {monthLabel(monthKeyStr)}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>{stateLine}</div>
          {canEdit ? (
            <>
              <div className="form-group">
                <label className="form-label">Amount <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(leave blank to use the calculated {fmtMoney(info.ruleAmount)})</span></label>
                <input className="form-control" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder={String(info.ruleAmount)} />
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <textarea className="form-control" rows={3} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. $80 overpayment, deduct from next month" style={{ resize: 'vertical' }} />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13.5 }}>
              <div style={{ marginBottom: 8 }}><strong>{fmtMoney(info.amount)}</strong>{info.hasOverride && <span style={{ color: 'var(--text-muted)' }}> (set by hand)</span>}</div>
              {info.note ? <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{info.note}</div> : <div style={{ color: 'var(--text-muted)' }}>No note.</div>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
          {canEdit && <button className="btn btn-gold" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── One month: paid date + bonuses ───────────────────────────────────────────
function MonthModal({ canEdit, monthKeyStr, total, payout, bonuses, onClose, onSaved }) {
  const toast = useToast()
  const [paidOn, setPaidOn] = useState(payout?.paid_on || '')
  const [payNote, setPayNote] = useState(payout?.note || '')
  const [bonusAmount, setBonusAmount] = useState('')
  const [bonusNote, setBonusNote] = useState('')
  const [saving, setSaving] = useState(false)

  const savePayout = async () => {
    setSaving(true)
    const { error } = await supabase.from('commission_payouts').upsert({
      month: monthStart(monthKeyStr), paid_on: paidOn || null, note: payNote.trim() || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'month' })
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Saved', 'success')
    onSaved()
    onClose()
  }

  const addBonus = async () => {
    if (!bonusAmount) return
    setSaving(true)
    const { error } = await supabase.from('commission_bonuses').insert({
      month: monthStart(monthKeyStr), amount: Number(bonusAmount), note: bonusNote.trim() || null,
    })
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    setBonusAmount(''); setBonusNote('')
    toast('Bonus added', 'success')
    onSaved()
  }

  const removeBonus = async (b) => {
    if (!window.confirm(`Remove the ${fmtMoney(b.amount)} bonus?`)) return
    const { error } = await supabase.from('commission_bonuses').delete().eq('id', b.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <span className="modal-title">{monthLabel(monthKeyStr)} · {fmtMoney(total)}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Bonuses</div>
          {bonuses.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 10 }}>None this month.</div>}
          {bonuses.map(b => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
              <strong>{fmtMoney(b.amount)}</strong>
              <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{b.note}</span>
              {canEdit && <button className="btn-icon" onClick={() => removeBonus(b)} title="Remove"><Trash2 size={13} /></button>}
            </div>
          ))}
          {canEdit && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
              <input className="form-control" type="number" step="0.01" placeholder="Amount" value={bonusAmount}
                onChange={e => setBonusAmount(e.target.value)} style={{ width: 110 }} />
              <input className="form-control" placeholder="Note, e.g. 6 clients in one month" value={bonusNote}
                onChange={e => setBonusNote(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-ghost btn-sm" onClick={addBonus} disabled={saving || !bonusAmount}><Plus size={13} /></button>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: canEdit ? 0 : 14 }}>Payout</div>
          {canEdit ? (
            <>
              <div className="form-group">
                <label className="form-label">Date paid</label>
                <input className="form-control" type="date" value={paidOn} onChange={e => setPaidOn(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <textarea className="form-control" rows={2} value={payNote} onChange={e => setPayNote(e.target.value)} style={{ resize: 'vertical' }} />
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13.5 }}>
              {payout?.paid_on ? <>Paid {fmtDate(payout.paid_on)}</> : <span style={{ color: 'var(--text-muted)' }}>Not paid yet.</span>}
              {payout?.note && <div style={{ color: 'var(--text-secondary)', marginTop: 6, whiteSpace: 'pre-wrap' }}>{payout.note}</div>}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
          {canEdit && <button className="btn btn-gold" onClick={savePayout} disabled={saving}>{saving ? 'Saving...' : 'Save payout'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── One client: details, link, retainer changes ─────────────────────────────
function DealModal({ canEdit, deal, client, allClients, currentUser, changes, onClose, onSaved, onChangesSaved }) {
  const toast = useToast()
  const isNew = !deal.id
  const [form, setForm] = useState({
    client_id: deal.client_id || '',
    client_name: deal.client_name || '',
    start_date: deal.start_date || '',
    monthly_retainer: deal.monthly_retainer ?? '',
    terminated_on: deal.terminated_on || '',
    notes: deal.notes || '',
  })
  const [chMonth, setChMonth] = useState('')
  const [chAmount, setChAmount] = useState('')
  const [chNote, setChNote] = useState('')
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const sortedClients = useMemo(
    () => [...allClients].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allClients]
  )

  const pickClient = (id) => {
    const c = allClients.find(x => x.id === id)
    setForm(f => ({ ...f, client_id: id, client_name: c ? c.name : f.client_name, start_date: f.start_date || c?.start_date || '' }))
  }

  const earnWindow = form.start_date ? dealWindow({ start_date: form.start_date }) : null
  const linkedClient = form.client_id ? allClients.find(c => c.id === form.client_id) : client
  const term = terminationDate({ terminated_on: form.terminated_on || null }, linkedClient)
  const termSource = form.terminated_on ? 'set here' : linkedClient?.terminated_on ? 'from the client' : linkedClient?.archived_at ? 'from the archive date' : null

  const save = async () => {
    if (!form.client_name.trim() || !form.start_date || form.monthly_retainer === '') return
    setSaving(true)
    const payload = {
      client_id: form.client_id || null,
      client_name: form.client_name.trim(),
      start_date: form.start_date,
      monthly_retainer: Number(form.monthly_retainer),
      terminated_on: form.terminated_on || null,
      notes: form.notes.trim() || null,
    }
    const { error } = isNew
      ? await supabase.from('commission_deals').insert({ ...payload, created_by: currentUser?.id || null })
      : await supabase.from('commission_deals').update(payload).eq('id', deal.id)
    setSaving(false)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast(isNew ? 'Added' : 'Saved', 'success')
    onSaved()
  }

  const remove = async () => {
    if (!window.confirm(`Delete commission for ${deal.client_name}? Its notes and retainer changes go too. This can't be undone.`)) return
    const { error } = await supabase.from('commission_deals').delete().eq('id', deal.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    toast('Deleted', 'success')
    onSaved()
  }

  const addChange = async () => {
    if (!chMonth || chAmount === '') return
    const { error } = await supabase.from('commission_retainer_changes').insert({
      deal_id: deal.id, effective_month: `${chMonth}-01`, monthly_retainer: Number(chAmount), note: chNote.trim() || null,
    })
    if (error) { toast('Error: ' + error.message, 'error'); return }
    setChMonth(''); setChAmount(''); setChNote('')
    toast('Retainer change saved', 'success')
    onChangesSaved()
  }

  const removeChange = async (c) => {
    if (!window.confirm('Remove this retainer change?')) return
    const { error } = await supabase.from('commission_retainer_changes').delete().eq('id', c.id)
    if (error) { toast('Error: ' + error.message, 'error'); return }
    onChangesSaved()
  }

  const ro = !canEdit
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <span className="modal-title">{isNew ? 'Add commission' : deal.client_name}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Client in the app</label>
            <select className="form-control" value={form.client_id} onChange={e => pickClient(e.target.value)} disabled={ro}>
              <option value="">Not linked</option>
              {sortedClients.map(c => <option key={c.id} value={c.id}>{c.name}{c.archived_at ? ' (archived)' : ''}</option>)}
            </select>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Linking means archiving the client stops commission automatically.
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Name on the commission sheet</label>
              <input className="form-control" value={form.client_name} onChange={e => set('client_name', e.target.value)} disabled={ro} />
            </div>
            <div className="form-group">
              <label className="form-label">Start date</label>
              <input className="form-control" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} disabled={ro} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Monthly retainer (full amount the client pays)</label>
              <input className="form-control" type="number" step="0.01" value={form.monthly_retainer} onChange={e => set('monthly_retainer', e.target.value)} disabled={ro} />
              {form.monthly_retainer !== '' && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Commission: {fmtMoney(commissionFor(form.monthly_retainer))}/mo</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Termination date override</label>
              <input className="form-control" type="date" value={form.terminated_on} onChange={e => set('terminated_on', e.target.value)} disabled={ro} />
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                {term ? <>Terminated {fmtDate(term)} ({termSource}).</> : 'Leave blank to use the client\'s termination date.'}
              </div>
            </div>
          </div>
          {earnWindow && (
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Earns {monthLabel(earnWindow[0])} – {monthLabel(earnWindow[earnWindow.length - 1])}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} disabled={ro} style={{ resize: 'vertical' }} />
          </div>

          {!isNew && (
            <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 12, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Retainer changes</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
                For downgrades. Commission becomes 5% of the new amount from that month on; the 6-month window stays the same.
              </div>
              {changes.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 8 }}>None.</div>}
              {changes.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
                  <span style={{ minWidth: 70 }}>{monthLabel(monthKey(c.effective_month))}</span>
                  <strong>{fmtMoney(c.monthly_retainer)}</strong>
                  <span style={{ color: 'var(--text-muted)' }}>→ {fmtMoney(commissionFor(c.monthly_retainer))}/mo</span>
                  <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{c.note}</span>
                  {canEdit && <button className="btn-icon" onClick={() => removeChange(c)} title="Remove"><Trash2 size={13} /></button>}
                </div>
              ))}
              {canEdit && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <input className="form-control" type="month" value={chMonth} onChange={e => setChMonth(e.target.value)} style={{ width: 150 }} title="Effective month" />
                  <input className="form-control" type="number" step="0.01" placeholder="New retainer" value={chAmount} onChange={e => setChAmount(e.target.value)} style={{ width: 120 }} />
                  <input className="form-control" placeholder="Note" value={chNote} onChange={e => setChNote(e.target.value)} style={{ flex: 1, minWidth: 100 }} />
                  <button className="btn btn-ghost btn-sm" onClick={addChange} disabled={!chMonth || chAmount === ''}><Plus size={13} /> Add</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {canEdit && !isNew && (
            <button className="btn btn-ghost" onClick={remove} style={{ marginRight: 'auto', color: 'var(--red)' }}><Trash2 size={13} /> Delete</button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
          {canEdit && (
            <button className="btn btn-gold" onClick={save}
              disabled={saving || !form.client_name.trim() || !form.start_date || form.monthly_retainer === ''}>
              {saving ? 'Saving...' : isNew ? 'Add' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
